import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthTwinTopic, liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthBusinessTwin, GrowthTwinService } from './growth-twin.service';
import { safeJsonObject } from './growth-optimization.service';

@Component({
  standalone: true,
  selector: 'ain-growth-business-twin',
  imports: [
    RouterLink,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinCanAccessDirective,
    ButtonModule,
    AinDatePipe
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.twin.businessTwin.title')" [description]="t('growth.twin.businessTwin.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.twin.businessTwin.help.title'"
        [descriptionKey]="'growth.twin.businessTwin.help.description'"
        [bulletKeys]="['growth.twin.businessTwin.help.bullets.snapshot', 'growth.twin.businessTwin.help.bullets.shadow']" />

      <section class="card">
        <div class="toolbar">
          <button
            ainCanAccess
            [ainCanAccessPermission]="'growth.twin.manage'"
            pButton
            type="button"
            [label]="t('growth.twin.businessTwin.actions.refresh')"
            [loading]="refreshing()"
            (click)="refresh()"></button>
          <a routerLink="/growth/twin/scenarios" pButton severity="secondary" [label]="t('growth.twin.businessTwin.actions.viewScenarios')"></a>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
      </section>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!twin()) {
        <ain-state type="empty" [title]="t('growth.twin.businessTwin.empty')" />
      } @else {
        <section class="card shadow-note">
          <p>{{ t('growth.twin.businessTwin.disclaimer.shadow') }}</p>
        </section>

        <section class="card">
          <div class="grid">
            <div><strong>{{ t('growth.twin.businessTwin.fields.name') }}:</strong> {{ twin()!.name }}</div>
            <div><strong>{{ t('growth.twin.businessTwin.fields.status') }}:</strong> {{ twin()!.status }}</div>
            <div><strong>{{ t('growth.twin.businessTwin.fields.boundary') }}:</strong> {{ twin()!.boundary }}</div>
            <div><strong>{{ t('growth.twin.businessTwin.fields.disclaimer') }}:</strong> {{ twin()!.disclaimer }}</div>
            <div>
              <strong>{{ t('growth.twin.businessTwin.fields.lastRefreshedAt') }}:</strong>
              {{ twin()!.lastRefreshedAtUtc ? (twin()!.lastRefreshedAtUtc | ainDate:'datetime') : '-' }}
            </div>
          </div>
        </section>

        <section class="card">
          <h3>{{ t('growth.twin.businessTwin.fields.intelligenceSnapshot') }}</h3>
          @if (snapshotEntries().length === 0) {
            <p>-</p>
          } @else {
            @for (entry of snapshotEntries(); track entry.key) {
              <p><strong>{{ entry.key }}:</strong> {{ entry.value }}</p>
            }
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .shadow-note { background: #fffbeb; border-color: #fcd34d; color: #92400e; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
  `]
})
export class GrowthBusinessTwinComponent implements OnInit {
  private readonly service = inject(GrowthTwinService);
  private readonly i18n = inject(LocalizationService);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(false);
  readonly refreshing = signal(false);
  readonly twin = signal<GrowthBusinessTwin | null>(null);

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthTwinChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load();
  }

  t(key: string): string { return this.i18n.translate(key); }

  snapshotEntries(): Array<{ key: string; value: string }> {
    const object = safeJsonObject(this.twin()?.intelligenceSnapshotJson);
    return Object.keys(object).map(key => ({ key, value: `${object[key]}` }));
  }

  load(): void {
    this.loading.set(true);
    this.service.getBusinessTwin().subscribe({
      next: row => {
        this.twin.set(row);
        this.loading.set(false);
      },
      error: () => {
        this.twin.set(null);
        this.loading.set(false);
      }
    });
  }

  refresh(): void {
    this.refreshing.set(true);
    this.service.refreshBusinessTwin(this.twin()?.name).subscribe({
      next: row => {
        this.twin.set(row);
        this.refreshing.set(false);
      },
      error: () => this.refreshing.set(false)
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthTwinTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
